import React, { useCallback, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { beautyGetCommissionsApi, beautySaveCommissionsApi } from "../../api/organizerBeautyApi";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import {
  aNumero,
  estadoDeComisiones,
  faltaComisiones,
  hace,
  payloadComisiones,
  textoDeValor,
  valorDeCelda,
  valorDeProducto,
} from "./comisionesPayload";
import { BarraAccionesContext } from "./barraAcciones";
import { OrgToast } from "./TurnosUi";
import "./turnos-beauty.css";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   ComisionesTab — reglas, comisión por servicios (profesional x servicio) y por productos.
   Solo dueño (el back cierra con requireDueno). Prototipo Zoco Beauty, artboard "Comisiones".
───────────────────────────────────────── */

const leer = (res) => res?.data?.info ?? res?.data ?? null;

const hint = (n) => (Number.isNaN(n) ? "" : String(n));

const InputPorcentaje = ({ label, value, placeholder, onChange }) => (
  <span className="zr-com-pct">
    <input
      className="ef-input zr-com-pct__input"
      type="number"
      inputMode="decimal"
      min="0"
      max="100"
      step="any"
      aria-label={label}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
    <span className="zr-com-pct__sufijo">%</span>
  </span>
);

function CeldaModal({ celda, profesional, servicio, heredado, onChange, onAplicar, onUsarProfesional, onCancelar }) {
  const esFijo = celda.type === "fixed";
  const numVal = aNumero(celda.value);
  const valido = !Number.isNaN(numVal) && (esFijo ? numVal >= 0 : numVal >= 0 && numVal <= 100);

  return (
    <div className="zr-overlay" onMouseDown={onCancelar}>
      <div
        className="zr-modal zr-com-modal"
        role="dialog"
        aria-label={`Comisión de ${profesional.name} en ${servicio.name}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="zr-com-modal__header">
          <div>
            <span className="zr-com-modal__badge">Comisión específica</span>
            <h3 className="zr-modal__title zr-com-modal__title">{servicio.name}</h3>
          </div>
          <button
            type="button"
            className="zr-com-modal__close-btn"
            onClick={onCancelar}
            aria-label="Cerrar modal"
          >
            ✕
          </button>
        </div>

        <p className="zr-modal__text zr-com-modal__desc">
          Personalizá la comisión de <strong>{profesional.name}</strong> para este servicio.
        </p>

        {heredado && (
          <div className="zr-com-modal__heredado">
            <span className="zr-com-modal__heredado-label">Valor por defecto:</span>
            <span className="zr-com-modal__heredado-val">{heredado}</span>
            <span className="zr-com-modal__heredado-hint">(si no se define un valor propio)</span>
          </div>
        )}

        <div className="zr-com-modal__tipo-wrapper">
          <label className="zr-com-modal__field-label">Tipo de cálculo</label>
          <div className="zr-com-modal__tipo" role="group" aria-label="Tipo de comisión">
            <button
              type="button"
              className={"zr-com-tipo" + (!esFijo ? " zr-com-tipo--activo" : "")}
              aria-pressed={!esFijo}
              onClick={() => onChange({ ...celda, type: "percent" })}
            >
              <span aria-hidden="true">%</span> Porcentaje
            </button>
            <button
              type="button"
              className={"zr-com-tipo" + (esFijo ? " zr-com-tipo--activo" : "")}
              aria-pressed={esFijo}
              onClick={() => onChange({ ...celda, type: "fixed" })}
            >
              <span aria-hidden="true">$</span> Monto fijo
            </button>
          </div>
        </div>

        <div className="zr-com-modal__valor-wrap">
          <label htmlFor="com-modal-input-valor" className="zr-com-modal__field-label">
            {esFijo ? "Monto fijo por servicio" : "Porcentaje sobre el servicio"}
          </label>
          <div className="zr-com-modal__input-box">
            <span className="zr-com-modal__input-affix">{esFijo ? "$" : "%"}</span>
            <input
              id="com-modal-input-valor"
              className="ef-input zr-com-modal__input"
              type="number"
              inputMode="decimal"
              min="0"
              max={esFijo ? undefined : "100"}
              step="any"
              aria-label="Valor"
              placeholder={esFijo ? "Ej: 2500" : "Ej: 20"}
              autoFocus
              value={celda.value}
              onChange={(e) => onChange({ ...celda, value: e.target.value })}
            />
          </div>
        </div>

        <div className="zr-modal__actions zr-com-modal__actions">
          <button
            type="button"
            className="zr-btn zr-btn--ghost zr-com-modal__btn-reset"
            onClick={onUsarProfesional}
            title="Quitar personalización y heredar"
          >
            Usar el del profesional
          </button>
          <div className="zr-com-modal__actions-main">
            <button type="button" className="zr-btn zr-btn--ghost" onClick={onCancelar}>
              Cancelar
            </button>
            <button
              type="button"
              className="zr-btn zr-btn--dark zr-com-modal__btn-apply"
              onClick={onAplicar}
              disabled={!valido}
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const ComisionesTab = () => {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [celda, setCelda] = useState(null); // { proId, serviceId, type, value }
  const [toast, setToast] = useState("");
  const barra = useContext(BarraAccionesContext);
  const cerrarToast = useCallback(() => setToast(""), []);

  const cargar = useCallback(async () => {
    setLoading(true);
    setErrorCarga("");
    try {
      setEstado(estadoDeComisiones(leer(await beautyGetCommissionsApi())));
    } catch (e) {
      setErrorCarga(e?.response?.data?.message || "No pudimos cargar las comisiones. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const setSalon = (patch) => setEstado((prev) => ({ ...prev, ...patch }));
  const setPro = (proId, patch) =>
    setEstado((prev) => ({
      ...prev,
      professionals: prev.professionals.map((p) => (p.id === proId ? { ...p, ...patch } : p)),
    }));
  const setOverride = (proId, serviceId, override) =>
    setEstado((prev) => ({
      ...prev,
      professionals: prev.professionals.map((p) =>
        p.id === proId ? { ...p, services: { ...p.services, [serviceId]: override } } : p
      ),
    }));

  const guardar = async () => {
    const falta = faltaComisiones(estado);
    if (falta) {
      setError(falta);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await beautySaveCommissionsApi(payloadComisiones(estado));
      const body = leer(res);
      if (body && Array.isArray(body.professionals)) setEstado(estadoDeComisiones(body));
      setToast("Comisiones guardadas");
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos guardar las comisiones. Probá de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SpinLoading />;

  if (errorCarga) {
    return (
      <div className="org-page-error">
        <p>{errorCarga}</p>
        <button type="button" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  if (!estado) return null;

  const acciones = (
    <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={saving}>
      {saving ? "Guardando..." : "Guardar cambios"}
    </button>
  );

  const abrirCelda = (p, s) => {
    const o = p.services[s.id];
    setCelda({ proId: p.id, serviceId: s.id, type: o?.type || "percent", value: o?.value ?? "" });
  };
  const celdaPro = celda ? estado.professionals.find((p) => p.id === celda.proId) : null;
  const celdaServicio = celda ? estado.services.find((s) => s.id === celda.serviceId) : null;
  // Lo que heredaría la celda si se la limpia.
  const heredadoDeCelda = celdaPro
    ? textoDeValor(valorDeCelda(estado, { ...celdaPro, services: { ...celdaPro.services, [celda.serviceId]: null } }, celda.serviceId))
    : "";

  return (
    <div className="zr-com">
      <section className="zr-panel zr-com-section" aria-label="Estado de las comisiones">
        <label className="zr-com-regla">
          <input type="checkbox" role="switch" className="zr-check"
            checked={estado.enabled} disabled={saving}
            onChange={(e) => setSalon({ enabled: e.target.checked })} />
          <span><b>Comisiones habilitadas</b><small>Al desactivar, se ocultan las comisiones en el PDV y los productos nuevos quedan sin atribuir. Se conservan las reglas y el historial; los servicios se siguen calculando con las reglas vigentes.</small></span>
        </label>
      </section>
      {(estado.professionals.length === 0 || estado.services.length === 0) && (
        <p className="zr-com-nota">{estado.professionals.length === 0
          ? "Cargá a tu equipo en Profesionales para configurar sus comisiones."
          : "Cargá tus servicios para configurar las comisiones."}</p>
      )}
      {error && (
        <div role="alert" className="zr-com-error">
          {error}
        </div>
      )}

      <section className="zr-panel zr-com-section" aria-labelledby="com-reglas">
        <div className="zr-section-head">
          <h2 id="com-reglas" className="zr-section-head__title">Cuándo se gana la comisión</h2>
          <p className="zr-section-head__hint">
            | Vale para todos los profesionales del salón. La liquidación la ves en el PDV, en el módulo Comisiones.
          </p>
        </div>
        <label className={"zr-com-regla" + (estado.onlyFullyPaid ? " zr-com-regla--activa" : "")}>
          <input
            type="checkbox"
            className="zr-check"
            checked={estado.onlyFullyPaid}
            onChange={(e) => setSalon({ onlyFullyPaid: e.target.checked })}
          />
          <span>
            <b>Solo turnos pagados completos</b>
            <small>La comisión aparece cuando el turno queda sin saldo. Con solo la seña, todavía no.</small>
          </span>
        </label>
        <p className="zr-com-nota">
          Un turno cancelado o con “No asistió” no paga comisión. Una venta anulada en el PDV la descuenta.
        </p>
      </section>

      <section className="zr-panel zr-com-section" aria-labelledby="com-servicios">
        <div className="zr-com-head">
          <div className="zr-section-head" style={{ margin: 0 }}>
            <h2 id="com-servicios" className="zr-section-head__title">Comisión por servicios</h2>
            <p className="zr-section-head__hint">
              | Cada profesional tiene su porcentaje. Tocá una celda para pisarlo en un servicio con otro % o un monto fijo.
            </p>
          </div>
          <div className="zr-com-salon">
            <span>Por defecto del salón</span>
            <InputPorcentaje
              label="Porcentaje por defecto del salón en servicios"
              value={estado.serviceDefaultPercent}
              onChange={(v) => setSalon({ serviceDefaultPercent: v })}
            />
          </div>
        </div>

        <div className="zr-com-tablewrap">
          <table className="zr-com-table">
            <thead>
              <tr>
                <th scope="col" className="zr-com-table__corner">SERVICIO</th>
                {estado.professionals.map((p) => (
                  <th scope="col" key={p.id}>{p.name}</th>
                ))}
              </tr>
              <tr className="zr-com-table__base">
                <th scope="row">Por defecto del profesional</th>
                {estado.professionals.map((p) => (
                  <td key={p.id}>
                    <InputPorcentaje
                      label={`Por defecto de ${p.name}`}
                      value={p.serviceDefaultPercent}
                      placeholder={hint(aNumero(estado.serviceDefaultPercent))}
                      onChange={(v) => setPro(p.id, { serviceDefaultPercent: v })}
                    />
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {estado.services.map((s) => (
                <tr key={s.id}>
                  <th scope="row">{s.name}</th>
                  {estado.professionals.map((p) => {
                    if (!hace(p, s.id)) {
                      return (
                        <td key={p.id}>
                          <span className="zr-com-nolohace">No lo hace</span>
                        </td>
                      );
                    }
                    const v = valorDeCelda(estado, p, s.id);
                    const texto = textoDeValor(v) || "Sin valor";
                    return (
                      <td key={p.id}>
                        <button
                          type="button"
                          className={"zr-com-celda" + (v.propia ? " zr-com-celda--propia" : "")}
                          aria-label={`${s.name} de ${p.name}: ${texto}${v.propia ? "" : " (hereda)"}`}
                          onClick={() => abrirCelda(p, s)}
                        >
                          {texto}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="zr-com-nota">
          <span className="zr-com-leyenda" />
          Valor propio de ese profesional en ese servicio. En gris, el que hereda.
        </p>
      </section>

      <section className="zr-panel zr-com-section" aria-labelledby="com-productos">
        <div className="zr-section-head">
          <h2 id="com-productos" className="zr-section-head__title">Comisión por productos</h2>
          <p className="zr-section-head__hint">
            | Sobre lo que cada profesional vende en el mostrador del PDV (shampoo, ceras, esmaltes). En Vender se elige a quién se le atribuye cada producto.
          </p>
        </div>
        <div className="zr-com-cards">
          <div className="zr-com-card zr-com-card--salon">
            <div className="zr-com-card__header">
              <span className="zr-com-card__tag">Salón</span>
              <p className="zr-com-card__label" title="Por defecto">Por defecto</p>
            </div>
            <div className="zr-com-card__body">
              <InputPorcentaje
                label="Porcentaje por defecto del salón en productos"
                value={estado.productDefaultPercent}
                onChange={(v) => setSalon({ productDefaultPercent: v })}
              />
            </div>
            <span className="zr-com-card__hint">Base para todo el equipo</span>
          </div>
          {estado.professionals.map((p) => {
            const v = valorDeProducto(estado, p);
            return (
              <div
                className={"zr-com-card" + (v.propia ? " zr-com-card--custom" : "")}
                key={p.id}
              >
                <div className="zr-com-card__header">
                  <div className="zr-com-card__avatar">{p.name.charAt(0).toUpperCase()}</div>
                  <p className="zr-com-card__label" title={p.name}>{p.name}</p>
                </div>
                <div className="zr-com-card__body">
                  <InputPorcentaje
                    label={`Productos de ${p.name}`}
                    value={p.productPercent}
                    placeholder={hint(aNumero(estado.productDefaultPercent))}
                    onChange={(val) => setPro(p.id, { productPercent: val })}
                  />
                </div>
                <span className={"zr-com-card__hint" + (v.propia ? " zr-com-card__hint--propio" : "")}>
                  {v.propia ? "● Valor propio" : "Hereda el del salón"}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {barra ? createPortal(acciones, barra) : <div className="zr-com-foot">{acciones}</div>}

      {celda && celdaPro && celdaServicio && (
        <CeldaModal
          celda={celda}
          profesional={celdaPro}
          servicio={celdaServicio}
          heredado={heredadoDeCelda}
          onChange={setCelda}
          onCancelar={() => setCelda(null)}
          onUsarProfesional={() => {
            setOverride(celda.proId, celda.serviceId, null);
            setCelda(null);
          }}
          onAplicar={() => {
            setOverride(celda.proId, celda.serviceId, { type: celda.type, value: celda.value });
            setCelda(null);
          }}
        />
      )}

      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  );
};

export default ComisionesTab;
